import { useMemo, useCallback } from "react";

const MAX_DISCOUNT_PERCENT = 20;

export function useQuoteCalculations(quotation: Partial<Quotation>) {
  const getDiscountedProductTotal = useCallback((product: QuoteProduct) => {
    const discount = Math.min(
      MAX_DISCOUNT_PERCENT,
      Math.max(0, product.discountPercent || 0)
    );
    return product.price * product.quantity * (1 - discount / 100);
  }, []);

  const getCalculatedM2BySheets = useCallback((product: QuoteProduct) => {
    const sheetsBase = product.sheetsNeeded || product.quantity || 0;
    if (product.widthCm && product.lengthCm && sheetsBase > 0) {
      const m2PerSheet = (product.widthCm / 100) * (product.lengthCm / 100);
      return Math.round(sheetsBase * m2PerSheet * 100) / 100;
    }
    return product.requestedM2 || 0;
  }, []);

  const getProductDiscountAmount = useCallback((product: QuoteProduct) => {
    const discount = Math.min(
      MAX_DISCOUNT_PERCENT,
      Math.max(0, product.discountPercent || 0)
    );
    return product.price * product.quantity * (discount / 100);
  }, []);

  const getAccessoryDiscountAmount = useCallback((accessory: QuoteProduct) => {
    const discount = Math.min(
      MAX_DISCOUNT_PERCENT,
      Math.max(0, accessory.discountPercent || 0)
    );
    return accessory.price * accessory.quantity * (discount / 100);
  }, []);

  const getProductAccessoryNeeds = useCallback(
    (product: QuoteProduct) => {
      const sheetsBase = product.sheetsNeeded || product.quantity || 0;
      const requestedM2Base = getCalculatedM2BySheets(product);

      const adhesiveQty =
        product.adhesiveName && product.adhesiveUnitsPerSheet
          ? Math.ceil(sheetsBase * product.adhesiveUnitsPerSheet)
          : 0;

      const sealantQty =
        product.sealantName && product.sealantCoverageM2PerQuart
          ? Math.ceil(requestedM2Base / product.sealantCoverageM2PerQuart)
          : 0;

      return { adhesiveQty, sealantQty };
    },
    [getCalculatedM2BySheets]
  );

  const normalizeAccessoryId = useCallback(
    (name: string) => `auto-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
    []
  );

  const buildAutoAccessories = useCallback(
    (products: QuoteProduct[]) => {
      const accessoriesMap = new Map<string, QuoteProduct>();

      const addAccessory = (
        name: string,
        price: number,
        quantity: number,
        unit: string
      ) => {
        if (quantity <= 0) return;
        const id = normalizeAccessoryId(name);
        const existing = accessoriesMap.get(id);

        if (existing) {
          accessoriesMap.set(id, {
            ...existing,
            quantity: existing.quantity + quantity,
          });
          return;
        }

        accessoriesMap.set(id, {
          id,
          name,
          quantity,
          price,
          discountPercent: 0,
          source: "auto",
          unit,
        });
      };

      products.forEach((product) => {
        const productM2BySheets = getCalculatedM2BySheets(product);
        if (
          product.adhesiveName &&
          product.adhesiveUnitsPerSheet &&
          product.adhesiveUnitPrice
        ) {
          const adhesiveQty = Math.ceil(
            (product.sheetsNeeded || 0) * product.adhesiveUnitsPerSheet
          );
          addAccessory(
            product.adhesiveName,
            product.adhesiveUnitPrice,
            adhesiveQty,
            "unidades"
          );
        }

        if (
          product.sealantName &&
          product.sealantCoverageM2PerQuart &&
          product.sealantQuartPrice
        ) {
          const sealantQty = Math.ceil(
            productM2BySheets / product.sealantCoverageM2PerQuart
          );
          addAccessory(
            product.sealantName,
            product.sealantQuartPrice,
            sealantQty,
            "unidades"
          );
        }
      });

      return Array.from(accessoriesMap.values());
    },
    [normalizeAccessoryId, getCalculatedM2BySheets]
  );

  const buildProductsWithAccessoryQuantities = useCallback(() => {
    return (quotation.products || []).map((product) => {
      const calculatedM2 = getCalculatedM2BySheets(product);
      const { adhesiveQty, sealantQty } = getProductAccessoryNeeds(product);

      return {
        ...product,
        requestedM2: calculatedM2,
        adhesiveQuantity: adhesiveQty,
        sealantQuantity: sealantQty,
      };
    });
  }, [quotation.products, getCalculatedM2BySheets, getProductAccessoryNeeds]);

  const calculateTotalM2 = useCallback(() => {
    const productsWithQuantities = buildProductsWithAccessoryQuantities();
    return (
      Math.round(
        productsWithQuantities.reduce(
          (sum, product) => sum + (product.requestedM2 || 0),
          0
        ) * 100
      ) / 100
    );
  }, [buildProductsWithAccessoryQuantities]);

  const applyExistingAutoAccessoryDiscounts = (
    autoAccessories: QuoteProduct[],
    existingAccessories: QuoteProduct[]
  ) => {
    const existingAutoDiscountMap = new Map(
      existingAccessories
        .filter((item) => item.source === "auto")
        .map((item) => [item.id, item.discountPercent || 0])
    );

    return autoAccessories.map((item) => ({
      ...item,
      discountPercent: existingAutoDiscountMap.get(item.id) || 0,
    }));
  };

  const calculateAccessoriesNeeded = useCallback(() => {
    const products = quotation.products || [];

    const totalM2 = products.reduce((total, product) => {
      if (product.requestedM2) {
        return total + product.requestedM2;
      }
      return total;
    }, 0);

    return {
      totalM2: Math.round(totalM2 * 100) / 100,
      autoAccessories: buildAutoAccessories(products),
    };
  }, [quotation.products, buildAutoAccessories]);

  const calculateTotals = useCallback(() => {
    const productsSubtotal =
      quotation.products?.reduce((sum, p) => sum + p.price * p.quantity, 0) ||
      0;
    const discountTotal =
      quotation.products?.reduce(
        (sum, p) => sum + getProductDiscountAmount(p),
        0
      ) || 0;
    const productsTotal = productsSubtotal - discountTotal;
    const accessoriesSubtotal =
      quotation.accessories?.reduce(
        (sum, a) => sum + a.price * a.quantity,
        0
      ) || 0;
    const accessoriesDiscountTotal =
      quotation.accessories?.reduce(
        (sum, a) => sum + getAccessoryDiscountAmount(a),
        0
      ) || 0;
    const accessoriesTotal = accessoriesSubtotal - accessoriesDiscountTotal;

    const shippingAmount = Number.isFinite(Number(quotation.shipping))
      ? Number(quotation.shipping)
      : 0;
    const otherAmount = Number.isFinite(Number(quotation.others))
      ? Number(quotation.others)
      : 0;
    const subtotal =
      productsTotal + accessoriesTotal + shippingAmount + otherAmount;
    const vat19 = subtotal * 0.19;
    const total = subtotal + vat19;

    return {
      subtotal,
      vat19,
      total,
      discountTotal: discountTotal + accessoriesDiscountTotal,
      productsDiscountTotal: discountTotal,
      accessoriesDiscountTotal: accessoriesDiscountTotal,
    };
  }, [
    quotation.products,
    quotation.accessories,
    quotation.shipping,
    quotation.others,
    getProductDiscountAmount,
    getAccessoryDiscountAmount,
  ]);

  const accessoriesCalculation = useMemo(
    () => calculateAccessoriesNeeded(),
    [calculateAccessoriesNeeded]
  );

  const totals = useMemo(() => calculateTotals(), [calculateTotals]);

  return {
    MAX_DISCOUNT_PERCENT,
    getDiscountedProductTotal,
    getCalculatedM2BySheets,
    getProductDiscountAmount,
    getAccessoryDiscountAmount,
    getProductAccessoryNeeds,
    buildAutoAccessories,
    applyExistingAutoAccessoryDiscounts,
    calculateAccessoriesNeeded,
    calculateTotals,
    buildProductsWithAccessoryQuantities,
    calculateTotalM2,
    accessoriesCalculation,
    totals,
  };
}
